import React, { useState, useEffect, useRef } from "react";
import {
  HiPlus,
  HiPencil,
  HiChevronDown,
  HiChevronRight,
  HiTrash,
  HiXMark,
  HiArrowUpTray,
} from "react-icons/hi2";
import {
  GlobalActivityCategory,
  GlobalActivityType,
  ActivityVariableDefinition,
} from "../../types/activity";
import { globalActivityService } from "../../services/globalActivityService";
import { UNIT_OPTIONS, DEFAULT_UNITS } from "../../utils/unitUtils";

// Available preset variable names
const PRESET_VARIABLES: string[] = [
  "Distance",
  "Incline",
  "Intensity",
  "Reps",
  "Rest",
  "Side",
  "Speed",
  "Time",
  "Weight",
];

const GlobalActivitiesManagement: React.FC = () => {
  // State for categories
  const [categories, setCategories] = useState<GlobalActivityCategory[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Activity types per category
  const [activityTypesByCategory, setActivityTypesByCategory] = useState<
    Record<string, GlobalActivityType[]>
  >({});

  // Category form state
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [categoryForm, setCategoryForm] = useState<{
    name: string;
    description: string;
  }>({
    name: "",
    description: "",
  });

  // Activity type form state
  const [isAddingActivityType, setIsAddingActivityType] = useState<
    string | null
  >(null);
  const [editingActivityType, setEditingActivityType] = useState<{
    categoryId: string;
    typeId: string;
  } | null>(null);
  const [activityTypeForm, setActivityTypeForm] = useState<{
    name: string;
    description: string;
    variables: ActivityVariableDefinition[];
    image: string;
  }>({
    name: "",
    description: "",
    variables: [],
    image: "",
  });

  // Custom variable state
  const [showCustomVariableInput, setShowCustomVariableInput] = useState(false);
  const [customVariableName, setCustomVariableName] = useState("");
  const [customVariableUnit, setCustomVariableUnit] = useState("units");
  const [customVariableUnitsList, setCustomVariableUnitsList] = useState<
    string[]
  >([]);

  // CSV upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvUploadCategoryId, setCsvUploadCategoryId] = useState<string | null>(null);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const [csvProgress, setCsvProgress] = useState<{
    current: number;
    total: number;
    currentName: string;
    errors: string[];
  }>({ current: 0, total: 0, currentName: "", errors: [] });

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedCategories = await globalActivityService.fetchCategories();
      setCategories(fetchedCategories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch categories"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityTypes = async (categoryId: string) => {
    try {
      const types = await globalActivityService.fetchActivityTypes(categoryId);
      setActivityTypesByCategory((prev) => ({
        ...prev,
        [categoryId]: types,
      }));
    } catch (err) {
      console.error("Error fetching activity types:", err);
    }
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(Array.from(prev));
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
        // Fetch activity types when expanding
        if (!activityTypesByCategory[categoryId]) {
          fetchActivityTypes(categoryId);
        }
      }
      return newSet;
    });
  };

  // Category operations
  const handleAddCategory = async () => {
    if (!categoryForm.name.trim()) return;

    try {
      await globalActivityService.createCategory({
        name: categoryForm.name,
        description: categoryForm.description,
      });
      setCategoryForm({ name: "", description: "" });
      setIsAddingCategory(false);
      fetchCategories();
    } catch (err) {
      console.error("Error creating category:", err);
      setError(err instanceof Error ? err.message : "Failed to create category");
    }
  };

  const handleUpdateCategory = async (categoryId: string) => {
    if (!categoryForm.name.trim()) return;

    try {
      await globalActivityService.updateCategory(categoryId, {
        name: categoryForm.name,
        description: categoryForm.description,
      });
      setCategoryForm({ name: "", description: "" });
      setEditingCategory(null);
      fetchCategories();
    } catch (err) {
      console.error("Error updating category:", err);
      setError(err instanceof Error ? err.message : "Failed to update category");
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this exercise category? All exercises within will also be deleted."
      )
    ) {
      return;
    }

    try {
      await globalActivityService.deleteCategory(categoryId);
      fetchCategories();
    } catch (err) {
      console.error("Error deleting category:", err);
      setError(err instanceof Error ? err.message : "Failed to delete category");
    }
  };

  const startEditingCategory = (category: GlobalActivityCategory) => {
    setEditingCategory(category.id || null);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
    });
  };

  // Activity Type operations
  const handleAddActivityType = async (categoryId: string) => {
    if (
      !activityTypeForm.name.trim() ||
      activityTypeForm.variables.length === 0
    ) {
      setError("Name and at least one variable are required");
      return;
    }

    try {
      await globalActivityService.createActivityType(categoryId, {
        name: activityTypeForm.name,
        description: activityTypeForm.description,
        variables: activityTypeForm.variables,
        image: activityTypeForm.image,
      });
      resetActivityTypeForm();
      setIsAddingActivityType(null);
      fetchActivityTypes(categoryId);
    } catch (err) {
      console.error("Error creating activity type:", err);
      setError(
        err instanceof Error ? err.message : "Failed to create activity type"
      );
    }
  };

  const handleUpdateActivityType = async (
    categoryId: string,
    typeId: string
  ) => {
    if (
      !activityTypeForm.name.trim() ||
      activityTypeForm.variables.length === 0
    ) {
      setError("Name and at least one variable are required");
      return;
    }

    try {
      await globalActivityService.updateActivityType(categoryId, typeId, {
        name: activityTypeForm.name,
        description: activityTypeForm.description,
        variables: activityTypeForm.variables,
        image: activityTypeForm.image,
      });
      resetActivityTypeForm();
      setEditingActivityType(null);
      fetchActivityTypes(categoryId);
    } catch (err) {
      console.error("Error updating activity type:", err);
      setError(
        err instanceof Error ? err.message : "Failed to update activity type"
      );
    }
  };

  const handleDeleteActivityType = async (
    categoryId: string,
    typeId: string
  ) => {
    if (!window.confirm("Are you sure you want to delete this exercise?")) {
      return;
    }

    try {
      await globalActivityService.deleteActivityType(categoryId, typeId);
      fetchActivityTypes(categoryId);
    } catch (err) {
      console.error("Error deleting activity type:", err);
      setError(
        err instanceof Error ? err.message : "Failed to delete activity type"
      );
    }
  };

  const startEditingActivityType = (
    categoryId: string,
    activityType: GlobalActivityType
  ) => {
    setEditingActivityType({
      categoryId,
      typeId: activityType.id || "",
    });
    setActivityTypeForm({
      name: activityType.name,
      description: activityType.description,
      variables: activityType.variables as ActivityVariableDefinition[],
      image: activityType.image || "",
    });
    setIsAddingActivityType(null);
  };

  const resetActivityTypeForm = () => {
    setActivityTypeForm({
      name: "",
      description: "",
      variables: [],
      image: "",
    });
    setShowCustomVariableInput(false);
    setCustomVariableName("");
    setCustomVariableUnit("units");
    setCustomVariableUnitsList([]);
  };

  // CSV Upload Functions
  
  // Helper to find preset variable name with case-insensitive matching
  const findPresetVariableName = (name: string): string | null => {
    const lowerName = name.toLowerCase();
    const preset = PRESET_VARIABLES.find((v) => v.toLowerCase() === lowerName);
    return preset || null;
  };

  const parseVariablesFromCsv = (variablesStr: string): ActivityVariableDefinition[] => {
    // Parse format like: "weight (kg, lbs), Reps (number), Sets (number), Rest (mins), Side (left/right/both)"
    const variables: ActivityVariableDefinition[] = [];
    
    // Split by comma but respect parentheses
    const parts: string[] = [];
    let current = "";
    let parenDepth = 0;
    
    for (const char of variablesStr) {
      if (char === "(") parenDepth++;
      if (char === ")") parenDepth--;
      if (char === "," && parenDepth === 0) {
        parts.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    if (current.trim()) parts.push(current.trim());

    for (const part of parts) {
      // Parse "variableName (unit1, unit2)" or "variableName (unit1/unit2/unit3)" format
      const match = part.match(/^([^(]+)\s*\(([^)]+)\)$/);
      if (match) {
        let name = match[1].trim();
        const unitsStr = match[2];
        
        // Split units by comma OR slash
        const unitsList = unitsStr
          .split(/[,/]/)
          .map((u) => u.trim())
          .filter(Boolean);
        const defaultUnit = unitsList[0] || "units";
        
        // Check if this matches a preset variable (case-insensitive)
        const presetName = findPresetVariableName(name);
        if (presetName) {
          name = presetName; // Use the properly cased preset name
        }
        
        variables.push({
          name,
          defaultUnit,
          unitsList: unitsList.length > 1 ? unitsList : undefined,
        });
      } else {
        // No units specified, just use the name
        let name = part.trim();
        if (name) {
          // Check if this matches a preset variable (case-insensitive)
          const presetName = findPresetVariableName(name);
          if (presetName) {
            name = presetName;
            // Use default unit from preset
            const defaultUnit = DEFAULT_UNITS[presetName] || "units";
            variables.push({
              name,
              defaultUnit,
            });
          } else {
            variables.push({
              name,
              defaultUnit: "units",
            });
          }
        }
      }
    }

    return variables;
  };

  const parseCsvContent = (csvText: string): Array<{
    name: string;
    description: string;
    variables: ActivityVariableDefinition[];
  }> => {
    const lines = csvText.split("\n").map((line) => line.trim()).filter((line) => line);
    
    // Skip header row
    const dataLines = lines.slice(1);
    const activities: Array<{
      name: string;
      description: string;
      variables: ActivityVariableDefinition[];
    }> = [];

    for (const line of dataLines) {
      // Parse CSV line handling quoted fields
      const fields: string[] = [];
      let currentField = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          fields.push(currentField.trim());
          currentField = "";
        } else {
          currentField += char;
        }
      }
      fields.push(currentField.trim());

      // fields[0] = Exercise Name
      // fields[1] = Description
      // fields[2] = Variables
      // fields[3] = Notes (ignored or could be appended to description)
      
      const name = fields[0] || "";
      const description = fields[1] || "";
      const variablesStr = fields[2] || "";

      if (name) {
        activities.push({
          name,
          description,
          variables: parseVariablesFromCsv(variablesStr),
        });
      }
    }

    return activities;
  };

  const handleCsvUpload = async (categoryId: string, file: File) => {
    try {
      setIsProcessingCsv(true);
      setCsvProgress({ current: 0, total: 0, currentName: "", errors: [] });

      const text = await file.text();
      const activities = parseCsvContent(text);

      if (activities.length === 0) {
        setError("No valid exercises found in CSV file.");
        setIsProcessingCsv(false);
        return;
      }

      setCsvProgress((prev) => ({ ...prev, total: activities.length }));

      const errors: string[] = [];

      for (let i = 0; i < activities.length; i++) {
        const activity = activities[i];
        setCsvProgress((prev) => ({
          ...prev,
          current: i + 1,
          currentName: activity.name,
        }));

        try {
          // Validate activity has at least one variable
          if (activity.variables.length === 0) {
            errors.push(`${activity.name}: No variables specified`);
            continue;
          }

          await globalActivityService.createActivityType(categoryId, {
            name: activity.name,
            description: activity.description,
            variables: activity.variables,
          });

          // Small delay to prevent overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (err) {
          errors.push(
            `${activity.name}: ${err instanceof Error ? err.message : "Failed to create"}`
          );
        }
      }

      setCsvProgress((prev) => ({ ...prev, errors }));

      // Refresh activity types
      await fetchActivityTypes(categoryId);

      // Show completion
      setTimeout(() => {
        setIsProcessingCsv(false);
        if (errors.length > 0) {
          setError(`Upload completed with ${errors.length} error(s). Check console for details.`);
          console.error("CSV upload errors:", errors);
        }
      }, 1000);
    } catch (err) {
      console.error("Error processing CSV:", err);
      setError("Error processing CSV file. Please check the format and try again.");
      setIsProcessingCsv(false);
    }
  };

  const triggerCsvUpload = (categoryId: string) => {
    setCsvUploadCategoryId(categoryId);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && csvUploadCategoryId) {
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        handleCsvUpload(csvUploadCategoryId, file);
      } else {
        setError("Please select a valid CSV file.");
      }
    }
    setCsvUploadCategoryId(null);
  };

  // Variable operations
  const toggleVariable = (variableName: string) => {
    setActivityTypeForm((prev) => {
      const existingIndex = prev.variables.findIndex(
        (v) => v.name === variableName
      );

      if (existingIndex !== -1) {
        return {
          ...prev,
          variables: prev.variables.filter((_, idx) => idx !== existingIndex),
        };
      } else {
        const defaultUnit = DEFAULT_UNITS[variableName] || "units";
        const unitsList = UNIT_OPTIONS[variableName] || UNIT_OPTIONS.Default;
        return {
          ...prev,
          variables: [
            ...prev.variables,
            { name: variableName, defaultUnit, unitsList },
          ],
        };
      }
    });
  };

  const updateVariableUnit = (variableName: string, newUnit: string) => {
    setActivityTypeForm((prev) => ({
      ...prev,
      variables: prev.variables.map((v) =>
        v.name === variableName ? { ...v, defaultUnit: newUnit } : v
      ),
    }));
  };

  const updateVariableUnitsList = (variableName: string, unitsList: string[]) => {
    setActivityTypeForm((prev) => ({
      ...prev,
      variables: prev.variables.map((v) =>
        v.name === variableName ? { ...v, unitsList } : v
      ),
    }));
  };

  const isVariableSelected = (variableName: string): boolean => {
    return activityTypeForm.variables.some((v) => v.name === variableName);
  };

  const getVariableUnit = (variableName: string): string => {
    const variable = activityTypeForm.variables.find(
      (v) => v.name === variableName
    );
    return variable?.defaultUnit || DEFAULT_UNITS[variableName] || "units";
  };

  const getVariableUnitsList = (variableName: string): string[] => {
    const variable = activityTypeForm.variables.find(
      (v) => v.name === variableName
    );
    return variable?.unitsList || UNIT_OPTIONS[variableName] || UNIT_OPTIONS.Default;
  };

  const addCustomVariable = () => {
    const trimmedName = customVariableName.trim();
    if (!trimmedName) return;

    // Check if already exists
    const existingNames = activityTypeForm.variables.map((v) =>
      v.name.toLowerCase()
    );
    if (
      existingNames.includes(trimmedName.toLowerCase()) ||
      PRESET_VARIABLES.map((v) => v.toLowerCase()).includes(
        trimmedName.toLowerCase()
      )
    ) {
      setError("This variable already exists");
      return;
    }

    const unitsList =
      customVariableUnitsList.length > 0
        ? customVariableUnitsList
        : [customVariableUnit];

    setActivityTypeForm((prev) => ({
      ...prev,
      variables: [
        ...prev.variables,
        { name: trimmedName, defaultUnit: customVariableUnit, unitsList },
      ],
    }));

    setCustomVariableName("");
    setCustomVariableUnit("units");
    setCustomVariableUnitsList([]);
    setShowCustomVariableInput(false);
    setError(null);
  };

  const addUnitToCustomList = (unit: string) => {
    const trimmed = unit.trim();
    if (trimmed && !customVariableUnitsList.includes(trimmed)) {
      setCustomVariableUnitsList((prev) => [...prev, trimmed]);
    }
  };

  const removeUnitFromCustomList = (unit: string) => {
    setCustomVariableUnitsList((prev) => prev.filter((u) => u !== unit));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading loading-spinner loading-lg"></div>
        <p className="ml-4 text-base-content/70">Loading exercises...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hidden file input for CSV upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />

      {/* CSV Processing Dialog */}
      {isProcessingCsv && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-base-100 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Processing CSV Upload</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm text-base-content/70 mb-1">
                  <span>Progress</span>
                  <span>
                    {csvProgress.current} / {csvProgress.total}
                  </span>
                </div>
                <div className="w-full bg-base-300 rounded-full h-2">
                  <div
                    className="bg-neutral h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${csvProgress.total > 0 ? (csvProgress.current / csvProgress.total) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
              {csvProgress.currentName && (
                <p className="text-sm text-base-content/70">
                  Adding: <span className="font-medium">{csvProgress.currentName}</span>
                </p>
              )}
              {csvProgress.errors.length > 0 && (
                <div className="text-sm text-red-600">
                  <p className="font-medium">{csvProgress.errors.length} error(s):</p>
                  <ul className="list-disc list-inside mt-1 max-h-32 overflow-y-auto">
                    {csvProgress.errors.slice(0, 5).map((err, i) => (
                      <li key={i} className="truncate">{err}</li>
                    ))}
                    {csvProgress.errors.length > 5 && (
                      <li>...and {csvProgress.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
              {csvProgress.current === csvProgress.total && csvProgress.total > 0 && (
                <p className="text-green-600 font-medium">
                  ✓ Upload complete!
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Activity Type Dialog */}
      {editingActivityType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Dialog Header */}
            <div className="flex items-center justify-between p-4 border-b border-base-300">
              <h3 className="text-lg font-semibold">Edit Exercise</h3>
              <button
                onClick={() => {
                  setEditingActivityType(null);
                  resetActivityTypeForm();
                }}
                className="p-1 hover:bg-base-200 rounded"
              >
                <HiXMark className="w-5 h-5" />
              </button>
            </div>

            {/* Dialog Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-base-content/80 mb-1">
                  Exercise Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., 5K Run, Bench Press"
                  className="w-full px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                  value={activityTypeForm.name}
                  onChange={(e) =>
                    setActivityTypeForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-base-content/80 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Describe this exercise..."
                  className="w-full px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent resize-none bg-base-100 text-base-content"
                  rows={2}
                  value={activityTypeForm.description}
                  onChange={(e) =>
                    setActivityTypeForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Variables Selection */}
              <div>
                <label className="block text-sm font-medium text-base-content/80 mb-2">
                  Variables *
                </label>
                <p className="text-xs text-base-content/60 mb-3">
                  Select which metrics users can track for this exercise
                </p>

                {/* Preset Variables */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {PRESET_VARIABLES.map((variableName) => {
                    const selected = isVariableSelected(variableName);
                    const availableUnits =
                      UNIT_OPTIONS[variableName] || UNIT_OPTIONS.Default;
                    const currentUnit = getVariableUnit(variableName);
                    const currentUnitsList = getVariableUnitsList(variableName);

                    return (
                      <div
                        key={variableName}
                        className={`p-3 rounded-lg border-2 transition-colors ${
                          selected
                            ? "border-base-content bg-base-100"
                            : "border-base-300 bg-base-100"
                        }`}
                      >
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleVariable(variableName)}
                            className="w-4 h-4 text-base-content bg-base-200 border-base-300 rounded focus:ring-base-content"
                          />
                          <span className="text-sm font-medium">
                            {variableName}
                          </span>
                        </label>

                        {selected && (
                          <div className="mt-2 space-y-2">
                            <div>
                              <label className="text-xs text-base-content/70">
                                Default Unit:
                              </label>
                              <select
                                value={currentUnit}
                                onChange={(e) =>
                                  updateVariableUnit(variableName, e.target.value)
                                }
                                className="w-full mt-1 px-2 py-1 text-xs border border-base-300 rounded bg-base-100 text-base-content"
                              >
                                {availableUnits.map((unit) => (
                                  <option key={unit} value={unit}>
                                    {unit}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-base-content/70">
                                Available Units:
                              </label>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {availableUnits.map((unit) => (
                                  <label
                                    key={unit}
                                    className="flex items-center gap-1 text-xs"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={currentUnitsList.includes(unit)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          updateVariableUnitsList(variableName, [
                                            ...currentUnitsList,
                                            unit,
                                          ]);
                                        } else {
                                          updateVariableUnitsList(
                                            variableName,
                                            currentUnitsList.filter((u) => u !== unit)
                                          );
                                        }
                                      }}
                                      className="w-3 h-3"
                                    />
                                    {unit}
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Custom Variables Display */}
                {activityTypeForm.variables
                  .filter((v) => !PRESET_VARIABLES.includes(v.name))
                  .map((customVar) => (
                    <div
                      key={customVar.name}
                      className="mb-3 p-3 rounded-lg border-2 border-blue-500/30 bg-blue-500/10"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-700">
                          {customVar.name} (Custom)
                        </span>
                        <button
                          onClick={() => toggleVariable(customVar.name)}
                          className="p-1 hover:bg-blue-500/15 rounded"
                        >
                          <HiXMark className="w-4 h-4 text-blue-600" />
                        </button>
                      </div>
                      <div className="mt-2 text-xs text-blue-600">
                        Default: {customVar.defaultUnit}
                        {customVar.unitsList && customVar.unitsList.length > 0 && (
                          <span> | Options: {customVar.unitsList.join(", ")}</span>
                        )}
                      </div>
                    </div>
                  ))}

                {/* Add Custom Variable (simplified for dialog) */}
                {!showCustomVariableInput ? (
                  <button
                    type="button"
                    onClick={() => setShowCustomVariableInput(true)}
                    className="flex items-center gap-2 p-3 rounded-lg border-2 border-dashed border-base-300 hover:border-base-content/50 hover:bg-base-200 transition-colors w-full text-left"
                  >
                    <HiPlus className="w-4 h-4 text-base-content/50" />
                    <span className="text-sm text-base-content/70">Add custom variable</span>
                  </button>
                ) : (
                  <div className="p-4 border border-base-300 rounded-lg bg-base-100 space-y-3">
                    <div>
                      <label className="text-sm font-medium text-base-content/80">
                        Variable Name
                      </label>
                      <input
                        type="text"
                        value={customVariableName}
                        onChange={(e) => setCustomVariableName(e.target.value)}
                        placeholder="e.g., Cadence, Power"
                        className="w-full mt-1 px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-base-content/80">
                        Default Unit
                      </label>
                      <input
                        type="text"
                        value={customVariableUnit}
                        onChange={(e) => setCustomVariableUnit(e.target.value)}
                        placeholder="e.g., bpm, watts"
                        className="w-full mt-1 px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={addCustomVariable}
                        disabled={!customVariableName.trim()}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        Add Variable
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomVariableInput(false);
                          setCustomVariableName("");
                          setCustomVariableUnit("units");
                          setCustomVariableUnitsList([]);
                        }}
                        className="px-4 py-2 bg-base-200 rounded-lg hover:bg-base-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {activityTypeForm.variables.length === 0 && (
                  <p className="text-xs text-red-500 mt-2">
                    *Please select at least one variable
                  </p>
                )}
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-base-content/80 mb-1">
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                  value={activityTypeForm.image}
                  onChange={(e) =>
                    setActivityTypeForm((prev) => ({
                      ...prev,
                      image: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-base-300">
              <button
                onClick={() => {
                  setEditingActivityType(null);
                  resetActivityTypeForm();
                }}
                className="px-4 py-2 text-base-content/80 bg-base-200 rounded-lg hover:bg-base-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleUpdateActivityType(
                    editingActivityType.categoryId,
                    editingActivityType.typeId
                  );
                }}
                disabled={
                  !activityTypeForm.name.trim() ||
                  activityTypeForm.variables.length === 0
                }
                className="px-4 py-2 bg-neutral text-white rounded-lg hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Exercise
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-semibold">Default Library</h1>
          <p className="text-base-content/70 mt-1">
            Manage exercise categories and exercises available to all users
          </p>
        </div>
        <button
          onClick={() => setIsAddingCategory(true)}
          className="bg-neutral text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-neutral/90 transition-colors"
        >
          <HiPlus className="w-5 h-5" />
          Add Exercise Category
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <HiXMark className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Add Category Form */}
      {isAddingCategory && (
        <div className="bg-base-100 border border-base-300 rounded-lg shadow-sm mb-6 p-6">
          <h3 className="text-lg font-semibold mb-4">Add New Exercise Category</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-base-content/80 mb-1">
                Exercise Category Name *
              </label>
              <input
                type="text"
                placeholder="e.g., Running, Strength Training"
                className="w-full px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                value={categoryForm.name}
                onChange={(e) =>
                  setCategoryForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-base-content/80 mb-1">
                Description
              </label>
              <textarea
                placeholder="Describe this exercise category..."
                className="w-full px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent resize-none bg-base-100 text-base-content"
                rows={2}
                value={categoryForm.description}
                onChange={(e) =>
                  setCategoryForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => {
                setIsAddingCategory(false);
                setCategoryForm({ name: "", description: "" });
              }}
              className="px-4 py-2 text-base-content/80 bg-base-200 rounded-lg hover:bg-base-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddCategory}
              disabled={!categoryForm.name.trim()}
              className="px-4 py-2 bg-neutral text-white rounded-lg hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Exercise Category
            </button>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="space-y-4">
        {categories.length === 0 ? (
          <div className="bg-base-100 border border-base-300 rounded-lg p-8 text-center">
            <p className="text-base-content/60">
              No exercise categories yet. Click "Add Exercise Category" to create your first one.
            </p>
          </div>
        ) : (
          categories.map((category) => (
            <div
              key={category.id}
              className="bg-base-100 border border-base-300 rounded-lg shadow-sm"
            >
              <div className="p-4">
                {/* Category Header */}
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() =>
                      category.id && toggleCategoryExpansion(category.id)
                    }
                  >
                    {expandedCategories.has(category.id || "") ? (
                      <HiChevronDown className="w-5 h-5 text-base-content/60" />
                    ) : (
                      <HiChevronRight className="w-5 h-5 text-base-content/60" />
                    )}
                    <div>
                      <h3 className="text-lg font-semibold">{category.name}</h3>
                      {category.description && (
                        <p className="text-sm text-base-content/60">
                          {category.description}
                        </p>
                      )}
                    </div>
                    <span className="ml-2 px-2 py-1 bg-blue-500/15 text-blue-700 dark:text-blue-400 text-xs rounded-full">
                      {activityTypesByCategory[category.id || ""]?.length || 0}{" "}
                      exercises
                    </span>
                  </div>

                  {/* Category Actions */}
                  {editingCategory === category.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        className="px-3 py-1 border border-base-300 rounded-lg text-sm w-40 bg-base-100 text-base-content"
                        value={categoryForm.name}
                        onChange={(e) =>
                          setCategoryForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          category.id && handleUpdateCategory(category.id);
                        }}
                        className="px-3 py-1 bg-neutral text-white rounded-lg text-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCategory(null);
                          setCategoryForm({ name: "", description: "" });
                        }}
                        className="px-3 py-1 bg-base-300 rounded-lg text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (category.id) {
                            setIsAddingActivityType(category.id);
                            setEditingActivityType(null);
                            resetActivityTypeForm();
                            // Expand the category to show the form
                            setExpandedCategories((prev) => {
                              const newSet = new Set(Array.from(prev));
                              newSet.add(category.id!);
                              return newSet;
                            });
                            // Fetch activity types if not already loaded
                            if (!activityTypesByCategory[category.id]) {
                              fetchActivityTypes(category.id);
                            }
                          }
                        }}
                        className="px-3 py-2 bg-base-200 rounded-lg text-sm flex items-center gap-1 hover:bg-base-300 transition-colors"
                      >
                        <HiPlus className="w-4 h-4" />
                        Add Exercise
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (category.id) {
                            triggerCsvUpload(category.id);
                          }
                        }}
                        disabled={isProcessingCsv}
                        className="px-3 py-2 bg-base-200 rounded-lg text-sm flex items-center gap-1 hover:bg-base-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <HiArrowUpTray className="w-4 h-4" />
                        Upload CSV
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditingCategory(category);
                        }}
                        className="p-2 bg-base-200 rounded-lg hover:bg-base-300 transition-colors"
                      >
                        <HiPencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          category.id && handleDeleteCategory(category.id);
                        }}
                        className="p-2 bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-500/15 transition-colors"
                      >
                        <HiTrash className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Expanded Content */}
                {expandedCategories.has(category.id || "") && (
                  <div className="mt-4 border-t border-base-200 pt-4">
                    {/* Activity Types Table */}
                    {activityTypesByCategory[category.id || ""]?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-base-300">
                              <th className="text-left py-2 px-3 text-sm font-medium text-base-content/70">
                                Exercise Name
                              </th>
                              <th className="text-left py-2 px-3 text-sm font-medium text-base-content/70">
                                Variables
                              </th>
                              <th className="text-left py-2 px-3 text-sm font-medium text-base-content/70">
                                Description
                              </th>
                              <th className="text-right py-2 px-3 text-sm font-medium text-base-content/70">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {activityTypesByCategory[category.id || ""]?.map(
                              (activityType) => (
                                <tr
                                  key={activityType.id}
                                  className="border-b border-base-200 last:border-b-0"
                                >
                                  <td className="py-3 px-3 font-medium">
                                    {activityType.name}
                                  </td>
                                  <td className="py-3 px-3">
                                    <div className="flex flex-wrap gap-1">
                                      {(activityType.variables as ActivityVariableDefinition[]).map(
                                        (variable, idx) => (
                                          <span
                                            key={idx}
                                            className="px-2 py-0.5 bg-base-200 text-base-content/80 text-xs rounded-full"
                                          >
                                            {typeof variable === "string"
                                              ? variable
                                              : `${variable.name} (${variable.defaultUnit})`}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-3 px-3 text-base-content/60 text-sm max-w-xs truncate">
                                    {activityType.description || "-"}
                                  </td>
                                  <td className="py-3 px-3 text-right">
                                    <div className="flex justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          if (category.id) {
                                            startEditingActivityType(
                                              category.id,
                                              activityType
                                            );
                                          }
                                        }}
                                        className="p-1.5 hover:bg-base-200 rounded"
                                      >
                                        <HiPencil className="w-4 h-4 text-base-content/70" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          if (category.id && activityType.id) {
                                            handleDeleteActivityType(
                                              category.id,
                                              activityType.id
                                            );
                                          }
                                        }}
                                        className="p-1.5 hover:bg-red-500/10 rounded"
                                      >
                                        <HiTrash className="w-4 h-4 text-red-500" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-base-content/60 text-center py-4">
                        No exercises in this category yet.
                      </p>
                    )}

                    {/* Add Activity Type Form (inline) */}
                    {isAddingActivityType === category.id && (
                      <div className="mt-4 p-4 bg-base-200 rounded-lg">
                        <h4 className="font-semibold mb-4">Add Exercise</h4>

                        <div className="space-y-4">
                          {/* Name */}
                          <div>
                            <label className="block text-sm font-medium text-base-content/80 mb-1">
                              Exercise Name *
                            </label>
                            <input
                              type="text"
                              placeholder="e.g., 5K Run, Bench Press"
                              className="w-full px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                              value={activityTypeForm.name}
                              onChange={(e) =>
                                setActivityTypeForm((prev) => ({
                                  ...prev,
                                  name: e.target.value,
                                }))
                              }
                            />
                          </div>

                          {/* Description */}
                          <div>
                            <label className="block text-sm font-medium text-base-content/80 mb-1">
                              Description
                            </label>
                            <textarea
                              placeholder="Describe this exercise..."
                              className="w-full px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent resize-none bg-base-100 text-base-content"
                              rows={2}
                              value={activityTypeForm.description}
                              onChange={(e) =>
                                setActivityTypeForm((prev) => ({
                                  ...prev,
                                  description: e.target.value,
                                }))
                              }
                            />
                          </div>

                          {/* Variables Selection */}
                          <div>
                            <label className="block text-sm font-medium text-base-content/80 mb-2">
                              Variables *
                            </label>
                            <p className="text-xs text-base-content/60 mb-3">
                              Select which metrics users can track for this
                              exercise
                            </p>

                            {/* Preset Variables */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                              {PRESET_VARIABLES.map((variableName) => {
                                const selected = isVariableSelected(variableName);
                                const availableUnits =
                                  UNIT_OPTIONS[variableName] ||
                                  UNIT_OPTIONS.Default;
                                const currentUnit = getVariableUnit(variableName);
                                const currentUnitsList =
                                  getVariableUnitsList(variableName);

                                return (
                                  <div
                                    key={variableName}
                                    className={`p-3 rounded-lg border-2 transition-colors ${
                                      selected
                                        ? "border-base-content bg-base-100"
                                        : "border-base-300 bg-base-100"
                                    }`}
                                  >
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() =>
                                          toggleVariable(variableName)
                                        }
                                        className="w-4 h-4 text-base-content bg-base-200 border-base-300 rounded focus:ring-base-content"
                                      />
                                      <span className="text-sm font-medium">
                                        {variableName}
                                      </span>
                                    </label>

                                    {selected && (
                                      <div className="mt-2 space-y-2">
                                        <div>
                                          <label className="text-xs text-base-content/70">
                                            Default Unit:
                                          </label>
                                          <select
                                            value={currentUnit}
                                            onChange={(e) =>
                                              updateVariableUnit(
                                                variableName,
                                                e.target.value
                                              )
                                            }
                                            className="w-full mt-1 px-2 py-1 text-xs border border-base-300 rounded bg-base-100 text-base-content"
                                          >
                                            {availableUnits.map((unit) => (
                                              <option key={unit} value={unit}>
                                                {unit}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="text-xs text-base-content/70">
                                            Available Units:
                                          </label>
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {availableUnits.map((unit) => (
                                              <label
                                                key={unit}
                                                className="flex items-center gap-1 text-xs"
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={currentUnitsList.includes(
                                                    unit
                                                  )}
                                                  onChange={(e) => {
                                                    if (e.target.checked) {
                                                      updateVariableUnitsList(
                                                        variableName,
                                                        [...currentUnitsList, unit]
                                                      );
                                                    } else {
                                                      updateVariableUnitsList(
                                                        variableName,
                                                        currentUnitsList.filter(
                                                          (u) => u !== unit
                                                        )
                                                      );
                                                    }
                                                  }}
                                                  className="w-3 h-3"
                                                />
                                                {unit}
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Custom Variables Display */}
                            {activityTypeForm.variables
                              .filter(
                                (v) => !PRESET_VARIABLES.includes(v.name)
                              )
                              .map((customVar) => (
                                <div
                                  key={customVar.name}
                                  className="mb-3 p-3 rounded-lg border-2 border-blue-500/30 bg-blue-500/10"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-blue-700">
                                      {customVar.name} (Custom)
                                    </span>
                                    <button
                                      onClick={() =>
                                        toggleVariable(customVar.name)
                                      }
                                      className="p-1 hover:bg-blue-500/15 rounded"
                                    >
                                      <HiXMark className="w-4 h-4 text-blue-600" />
                                    </button>
                                  </div>
                                  <div className="mt-2 text-xs text-blue-600">
                                    Default: {customVar.defaultUnit}
                                    {customVar.unitsList &&
                                      customVar.unitsList.length > 0 && (
                                        <span>
                                          {" "}
                                          | Options:{" "}
                                          {customVar.unitsList.join(", ")}
                                        </span>
                                      )}
                                  </div>
                                </div>
                              ))}

                            {/* Add Custom Variable */}
                            {!showCustomVariableInput ? (
                              <button
                                type="button"
                                onClick={() => setShowCustomVariableInput(true)}
                                className="flex items-center gap-2 p-3 rounded-lg border-2 border-dashed border-base-300 hover:border-base-content/50 hover:bg-base-200 transition-colors w-full text-left"
                              >
                                <HiPlus className="w-4 h-4 text-base-content/50" />
                                <span className="text-sm text-base-content/70">
                                  Add custom variable
                                </span>
                              </button>
                            ) : (
                              <div className="p-4 border border-base-300 rounded-lg bg-base-100 space-y-3">
                                <div>
                                  <label className="text-sm font-medium text-base-content/80">
                                    Variable Name
                                  </label>
                                  <input
                                    type="text"
                                    value={customVariableName}
                                    onChange={(e) =>
                                      setCustomVariableName(e.target.value)
                                    }
                                    placeholder="e.g., Cadence, Power"
                                    className="w-full mt-1 px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-base-content/80">
                                    Default Unit
                                  </label>
                                  <input
                                    type="text"
                                    value={customVariableUnit}
                                    onChange={(e) =>
                                      setCustomVariableUnit(e.target.value)
                                    }
                                    placeholder="e.g., bpm, watts"
                                    className="w-full mt-1 px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-base-content/80">
                                    Alternative Units (optional)
                                  </label>
                                  <div className="flex gap-2 mt-1">
                                    <input
                                      type="text"
                                      placeholder="Add unit..."
                                      className="flex-1 px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" &&
                                          (e.target as HTMLInputElement).value
                                        ) {
                                          e.preventDefault();
                                          addUnitToCustomList(
                                            (e.target as HTMLInputElement).value
                                          );
                                          (e.target as HTMLInputElement).value =
                                            "";
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        const input = (e.target as HTMLElement)
                                          .previousElementSibling as HTMLInputElement;
                                        if (input?.value) {
                                          addUnitToCustomList(input.value);
                                          input.value = "";
                                        }
                                      }}
                                      className="px-3 py-2 bg-base-200 rounded-lg hover:bg-base-300"
                                    >
                                      Add
                                    </button>
                                  </div>
                                  {customVariableUnitsList.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {customVariableUnitsList.map((unit) => (
                                        <span
                                          key={unit}
                                          className="px-2 py-1 bg-base-200 rounded-full text-sm flex items-center gap-1"
                                        >
                                          {unit}
                                          <button
                                            onClick={() =>
                                              removeUnitFromCustomList(unit)
                                            }
                                            className="hover:text-red-500"
                                          >
                                            <HiXMark className="w-3 h-3" />
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-2 pt-2">
                                  <button
                                    type="button"
                                    onClick={addCustomVariable}
                                    disabled={!customVariableName.trim()}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    Add Variable
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowCustomVariableInput(false);
                                      setCustomVariableName("");
                                      setCustomVariableUnit("units");
                                      setCustomVariableUnitsList([]);
                                    }}
                                    className="px-4 py-2 bg-base-200 rounded-lg hover:bg-base-300"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            {activityTypeForm.variables.length === 0 && (
                              <p className="text-xs text-red-500 mt-2">
                                *Please select at least one variable
                              </p>
                            )}
                          </div>

                          {/* Image URL */}
                          <div>
                            <label className="block text-sm font-medium text-base-content/80 mb-1">
                              Image URL (optional)
                            </label>
                            <input
                              type="url"
                              placeholder="https://..."
                              className="w-full px-3 py-2 border border-base-300 rounded-lg focus:ring-2 focus:ring-base-content focus:border-transparent bg-base-100 text-base-content"
                              value={activityTypeForm.image}
                              onChange={(e) =>
                                setActivityTypeForm((prev) => ({
                                  ...prev,
                                  image: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex justify-end gap-3 mt-6">
                          <button
                            onClick={() => {
                              setIsAddingActivityType(null);
                              resetActivityTypeForm();
                            }}
                            className="px-4 py-2 text-base-content/80 bg-base-200 rounded-lg hover:bg-base-300 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              if (category.id) {
                                handleAddActivityType(category.id);
                              }
                            }}
                            disabled={
                              !activityTypeForm.name.trim() ||
                              activityTypeForm.variables.length === 0
                            }
                            className="px-4 py-2 bg-neutral text-white rounded-lg hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Add Exercise
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GlobalActivitiesManagement;
